/**
 * 检索执行与容错（T12）。
 *
 * 把「开关 + query + 注入的 runner」收敛成一个**永不抛出**的结果对象：
 * - `ok`：拿到非空结果，交给上层做引用注入；
 * - `empty`：结果为空，给出提示但不阻断对话；
 * - `error`：结构化失败（沿用核心层 `SearchError`），给出提示但不阻断对话；
 * - `skipped`：开关关闭 / 空查询 / 用户取消，静默结束。
 *
 * 这里刻意不依赖任何网络实现：真实检索由注入的 `SearchRunner` 完成。
 */

import { SearchError } from '../../core/search';
import type { SearchResult } from '../../core/search';
import type { SearchRunner } from './provider';

/** 空结果时的非阻断提示。 */
export const SEARCH_EMPTY_HINT = '未找到相关搜索结果，已直接继续对话';
/** 检索失败时的非阻断提示前缀。 */
export const SEARCH_FAILED_HINT = '联网搜索失败，已直接继续对话';

export type SearchStatus = 'ok' | 'empty' | 'error' | 'skipped';

export interface SearchOutcome {
  status: SearchStatus;
  results: SearchResult[];
  /** 仅在 `empty` / `error` 时存在，供 UI 提示。 */
  message?: string;
  /** 仅在 `error` 时存在，保留结构化错误码便于后续分流。 */
  error?: SearchError;
}

export interface ExecuteSearchOptions {
  query: string;
  enabled: boolean;
  runner: SearchRunner;
  signal?: AbortSignal;
  limit?: number;
}

/** 把任意抛出物归一化成 `SearchError`，保证上层只面对结构化错误。 */
export function toSearchError(caught: unknown): SearchError {
  if (caught instanceof SearchError) return caught;
  const message = caught instanceof Error ? caught.message : String(caught);
  return new SearchError(message, 'unknown');
}

/** 执行一次检索；任何失败都转成结构化结果，绝不向上抛出。 */
export async function executeSearch(options: ExecuteSearchOptions): Promise<SearchOutcome> {
  if (!options.enabled) return { status: 'skipped', results: [] };
  const query = options.query.trim();
  if (query === '') return { status: 'skipped', results: [] };

  try {
    const results = await options.runner(query, {
      ...(options.signal === undefined ? {} : { signal: options.signal }),
      ...(options.limit === undefined ? {} : { limit: options.limit }),
    });
    const list = results;
    if (list.length === 0) {
      return { status: 'empty', results: [], message: SEARCH_EMPTY_HINT };
    }
    return { status: 'ok', results: list };
  } catch (caught) {
    const error = toSearchError(caught);
    // 取消沿用核心层语义：静默结束，不作为失败提示。
    if (error.code === 'aborted' || options.signal?.aborted === true) {
      return { status: 'skipped', results: [] };
    }
    return {
      status: 'error',
      results: [],
      message: `${SEARCH_FAILED_HINT}（${error.message}）`,
      error,
    };
  }
}
